import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Card, CardBody, CardHeader, Divider,
  Flex, Grid, HStack, Heading, Icon, Progress, Select, SimpleGrid, Spinner,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay,
  Stat, StatHelpText, StatLabel, StatNumber, Table, Tbody, Td, Text, Th, Thead,
  Tr, VStack, useDisclosure,
} from '@chakra-ui/react';
import {
  CheckCircleIcon, TimeIcon, WarningIcon, RepeatIcon, CalendarIcon,
} from '@chakra-ui/icons';
import {
  Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  obtenerEstadisticasOP,
  obtenerVigenciasProximasOP,
  EstadisticasOP,
  OpDocumentoOficial,
  FiltrosTramitesOP,
} from '../../../api/opTramiteApi';
import GestionTramitesOP from '../GestionTramites/GestionTramitesOP';

const ESTATUS_COLORS: Record<string, string> = {
  REGISTRADO: '#315f8f',
  EN_PROCESO: '#d4891f',
  EN_REVISION: '#d06a25',
  OBSERVADO: '#9a6a16',
  PENDIENTE_DOCUMENTACION: '#6c4aa5',
  PENDIENTE_PAGO: '#b83280',
  EN_INSPECCION: '#24737b',
  PROCEDENTE: '#2f855a',
  NO_PROCEDENTE: '#c53030',
  RECHAZADO: '#822727',
  CONCLUIDO: '#64748b',
  CANCELADO: '#9f7a7a',
};

const ESTATUS_LABELS: Record<string, string> = {
  REGISTRADO: 'Registrado',
  EN_PROCESO: 'En proceso',
  EN_REVISION: 'En revision',
  OBSERVADO: 'Observado',
  PENDIENTE_DOCUMENTACION: 'Pend. documentacion',
  PENDIENTE_PAGO: 'Pend. pago',
  EN_INSPECCION: 'En inspeccion',
  PROCEDENTE: 'Procedente',
  NO_PROCEDENTE: 'No procedente',
  RECHAZADO: 'Rechazado',
  CONCLUIDO: 'Concluido',
  CANCELADO: 'Cancelado',
};

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const ATENCION = ['REGISTRADO', 'EN_PROCESO', 'EN_REVISION', 'PENDIENTE_DOCUMENTACION', 'PENDIENTE_PAGO', 'EN_INSPECCION', 'OBSERVADO'];

const formatPct = (value: number) => `${Number.isFinite(value) ? value.toFixed(0) : 0}%`;

const monthRange = (year: number, month: number) => {
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  const toIso = (date: Date) => date.toISOString().split('T')[0];
  return { fechaInicio: toIso(start), fechaFin: toIso(end) };
};

const DashboardObrasPublicas: React.FC = () => {
  const [stats, setStats] = useState<EstadisticasOP | null>(null);
  const [vigencias, setVigencias] = useState<Array<OpDocumentoOficial & { tramite: any }>>([]);
  const [loading, setLoading] = useState(true);
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [gestionTitle, setGestionTitle] = useState('Gestion Integral OP');
  const [gestionFilters, setGestionFilters] = useState<FiltrosTramitesOP>({});
  const gestionModal = useDisclosure();

  const cargar = async () => {
    setLoading(true);
    try {
      const [estadisticas, docsVencen] = await Promise.all([
        obtenerEstadisticasOP(anno),
        obtenerVigenciasProximasOP(30),
      ]);
      setStats(estadisticas);
      setVigencias(docsVencen);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [anno]);

  const resumen = useMemo(() => {
    const porEstatus = stats?.porEstatus || [];
    const total = stats?.total || 0;
    const procedentes = porEstatus.find(e => e.estatus === 'PROCEDENTE')?._count?.estatus || 0;
    const concluidos = porEstatus.find(e => e.estatus === 'CONCLUIDO')?._count?.estatus || 0;
    const observados = porEstatus.find(e => e.estatus === 'OBSERVADO')?._count?.estatus || 0;
    const inspeccion = porEstatus.find(e => e.estatus === 'EN_INSPECCION')?._count?.estatus || 0;
    const pendientes = porEstatus
      .filter(e => ATENCION.includes(e.estatus))
      .reduce((sum, item) => sum + item._count.estatus, 0);
    const eficiencia = total > 0 ? ((procedentes + concluidos) / total) * 100 : 0;
    const presion = total > 0 ? (pendientes / total) * 100 : 0;

    return { total, procedentes, concluidos, pendientes, observados, inspeccion, eficiencia, presion };
  }, [stats]);

  const pieData = useMemo(() => (stats?.porEstatus || []).map(e => ({
    name: ESTATUS_LABELS[e.estatus] || e.estatus,
    estatus: e.estatus,
    value: e._count.estatus,
    color: ESTATUS_COLORS[e.estatus] || '#718096',
  })), [stats]);

  const monthlyData = useMemo(() => {
    const byMonth = new Map((stats?.porMes || []).map(m => [m.mes, Number(m.total)]));
    return MESES.map((mes, index) => ({ mes, tramites: byMonth.get(index + 1) || 0 }));
  }, [stats]);

  const topTipos = useMemo(() => (stats?.porTipoConNombre || []).slice(0, 7), [stats]);
  const maxTipo = Math.max(...topTipos.map(t => t.cantidad), 1);

  const abrirGestion = (title: string, filters: FiltrosTramitesOP = {}) => {
    setGestionTitle(title);
    setGestionFilters({ anno, ...filters });
    gestionModal.onOpen();
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="520px">
        <VStack spacing={4}>
          <Spinner size="xl" thickness="4px" color="#5b182b" />
          <Text color="gray.500">Cargando tablero de Obras Publicas...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box pt="79px" px={{ base: 3, md: 6 }} pb={8} bg="#f7f4ee" minH="100vh">
      <Box
        position="relative"
        overflow="hidden"
        borderRadius="28px"
        p={{ base: 5, md: 7 }}
        mb={6}
        color="white"
        bg="linear-gradient(135deg, #5b1424 0%, #8f2137 56%, #b8844d 100%)"
        boxShadow="0 24px 60px rgba(77, 27, 35, 0.28)"
      >
        <Box position="absolute" right="-60px" top="-70px" w="260px" h="260px" borderRadius="full" bg="rgba(255,255,255,.13)" />
        <Box position="absolute" right="160px" bottom="-100px" w="220px" h="220px" borderRadius="full" bg="rgba(255,255,255,.08)" />

        <Flex position="relative" justify="space-between" gap={4} align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }}>
          <Box>
            <Badge bg="whiteAlpha.300" color="white" px={3} py={1} borderRadius="full" mb={3}>
              Direccion de Obras Publicas
            </Badge>
            <Heading size={{ base: 'lg', md: 'xl' }} letterSpacing="-0.04em">
              Tablero operativo {anno}
            </Heading>
            <Text mt={2} color="whiteAlpha.850" maxW="680px">
              Seguimiento ejecutivo de tramites, documentos por vencer y cargas de atencion para priorizar revision, pago e inspeccion.
            </Text>
          </Box>

          <HStack bg="whiteAlpha.200" border="1px solid rgba(255,255,255,.35)" borderRadius="18px" p={2}>
            <Select
              w="130px"
              value={anno}
              onChange={e => setAnno(Number(e.target.value))}
              bg="white"
              color="#5c1c2a"
              border="0"
              fontWeight="bold"
            >
              {[anno - 2, anno - 1, anno, anno + 1].map(y => <option key={y} value={y}>{y}</option>)}
            </Select>
            <Button leftIcon={<RepeatIcon />} onClick={cargar} variant="solid" bg="white" color="#5b182b" _hover={{ bg: 'whiteAlpha.900' }}>
              Actualizar
            </Button>
            <Button onClick={() => abrirGestion('Gestion Integral OP', {})} variant="solid" bg="#241c18" color="white" _hover={{ bg: '#3b2a22' }}>
              Gestion Integral OP
            </Button>
          </HStack>
        </Flex>
      </Box>

      {vigencias.length > 0 && (
        <Alert status="warning" borderRadius="18px" mb={5} bg="#fff7dd" color="#614100" border="1px solid #f1d287">
          <AlertIcon />
          Hay {vigencias.length} documento(s) oficiales que vencen en los proximos 30 dias. Prioriza renovacion o notificacion.
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={4} mb={6}>
        <KpiCard icon={CalendarIcon} label="Total de tramites" value={resumen.total} note={`Registrados en ${anno}`} accent="#5b182b" onClick={() => abrirGestion(`Todos los tramites ${anno}`)} />
        <KpiCard icon={CheckCircleIcon} label="Procedentes / concluidos" value={resumen.procedentes + resumen.concluidos} note={`Eficiencia ${formatPct(resumen.eficiencia)}`} accent="#2f855a" onClick={() => abrirGestion('Tramites procedentes', { estatus: 'PROCEDENTE' })} />
        <KpiCard icon={TimeIcon} label="En atencion" value={resumen.pendientes} note={`Carga activa ${formatPct(resumen.presion)}`} accent="#d4891f" onClick={() => abrirGestion('Tramites en atencion', { estatus: ATENCION.join(',') })} />
        <KpiCard icon={WarningIcon} label="Vigencias criticas" value={vigencias.length} note="Vencen en 30 dias" accent="#c05621" />
      </SimpleGrid>

      <Grid templateColumns={{ base: '1fr', xl: '1.45fr .95fr' }} gap={5} mb={5}>
        <Panel title="Actividad mensual" subtitle="Volumen de tramites capturados por mes">
          <ResponsiveContainer width="100%" height={290}>
            <BarChart
              data={monthlyData}
              margin={{ top: 12, right: 10, left: -20, bottom: 0 }}
              onClick={(state: any) => {
                const payload = state?.activePayload?.[0]?.payload;
                if (!payload) return;
                const month = MESES.indexOf(payload.mes) + 1;
                abrirGestion(`Tramites de ${payload.mes} ${anno}`, monthRange(anno, month));
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2d8ce" />
              <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#6b625a' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#6b625a' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: 'rgba(161,38,61,.08)' }} />
              <Bar dataKey="tramites" fill="#5b182b" radius={[9, 9, 0, 0]} barSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Estado de cartera" subtitle="Distribucion por estatus">
          {pieData.length === 0 ? (
            <EmptyState text="Sin informacion para graficar." />
          ) : (
            <ResponsiveContainer width="100%" height={290}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={58}
                  outerRadius={100}
                  paddingAngle={2}
                  cursor="pointer"
                  onClick={(entry: any) => abrirGestion(`Tramites: ${entry.name}`, { estatus: entry.estatus })}
                >
                  {pieData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </Grid>

      <Grid templateColumns={{ base: '1fr', xl: '.95fr 1.25fr' }} gap={5}>
        <Panel title="Tramites mas solicitados" subtitle="Top de demanda ciudadana">
          <VStack align="stretch" spacing={4}>
            {topTipos.length === 0 && <EmptyState text="Sin tramites registrados." />}
            {topTipos.map((tipo, idx) => (
              <Box key={`${tipo.tipo}-${idx}`} cursor="pointer" onClick={() => abrirGestion(`Busqueda: ${tipo.tipo}`, { buscar: tipo.tipo })}>
                <HStack justify="space-between" mb={1}>
                  <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{tipo.tipo}</Text>
                  <Badge bg="#efe1d8" color="#7d2032" borderRadius="full">{tipo.cantidad}</Badge>
                </HStack>
                <Progress value={(tipo.cantidad / maxTipo) * 100} h="8px" borderRadius="full" bg="#eadfd5" sx={{ '& > div': { background: '#5b182b' } }} />
              </Box>
            ))}
          </VStack>
        </Panel>

        <Panel title="Documentos por vencer" subtitle="Vigencias oficiales de atencion inmediata">
          {vigencias.length === 0 ? (
            <Alert status="success" borderRadius="16px" bg="#eaf7ef" color="#24533b">
              <AlertIcon />
              No hay documentos proximos a vencer en los proximos 30 dias.
            </Alert>
          ) : (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Folio</Th>
                  <Th>Solicitante</Th>
                  <Th>Documento</Th>
                  <Th isNumeric>Vence</Th>
                </Tr>
              </Thead>
              <Tbody>
                {vigencias.slice(0, 9).map(v => (
                  <Tr key={v.id}>
                    <Td
                      fontFamily="mono"
                      fontSize="xs"
                      cursor="pointer"
                      color="#5b182b"
                      fontWeight="bold"
                      onClick={() => abrirGestion(`Tramite ${v.tramite?.folio || ''}`, { folio: v.tramite?.folio })}
                    >
                      {v.tramite?.folio || 'N/D'}
                    </Td>
                    <Td maxW="180px">
                      <Text fontSize="sm" noOfLines={1}>{v.tramite?.nombreSolicitante || 'N/D'}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme="orange">{v.tipoDocumento || 'Documento'}</Badge>
                    </Td>
                    <Td isNumeric color="#b45309" fontWeight="bold">
                      {v.fechaVencimiento ? new Date(v.fechaVencimiento).toLocaleDateString('es-MX') : 'N/D'}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Panel>
      </Grid>

      <Modal isOpen={gestionModal.isOpen} onClose={gestionModal.onClose} size="full" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg="#f7f4ee">
          <ModalHeader bg="#5b182b" color="white">
            {gestionTitle}
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody p={{ base: 3, md: 5 }}>
            <GestionTramitesOP embedded initialFilters={gestionFilters} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const KpiCard = ({
  icon,
  label,
  value,
  note,
  accent,
  onClick,
}: {
  icon: any;
  label: string;
  value: number;
  note: string;
  accent: string;
  onClick?: () => void;
}) => (
  <Card
    borderRadius="22px"
    border="1px solid #eadfd5"
    boxShadow="0 16px 34px rgba(53, 36, 28, 0.08)"
    bg="white"
    cursor={onClick ? 'pointer' : 'default'}
    transition="all .2s ease"
    _hover={onClick ? { transform: 'translateY(-3px)', boxShadow: '0 22px 42px rgba(53, 36, 28, 0.13)' } : undefined}
    onClick={onClick}
  >
    <CardBody>
      <HStack justify="space-between" align="flex-start">
        <Stat>
          <StatLabel color="#746b63" fontSize="xs" textTransform="uppercase" letterSpacing=".08em">{label}</StatLabel>
          <StatNumber color="#211b18" fontSize="4xl" letterSpacing="-0.05em">{value}</StatNumber>
          <StatHelpText mb={0} color="#8a817a">{note}</StatHelpText>
        </Stat>
        <Flex w="44px" h="44px" borderRadius="16px" align="center" justify="center" bg={`${accent}18`}>
          <Icon as={icon} color={accent} boxSize={5} />
        </Flex>
      </HStack>
    </CardBody>
  </Card>
);

const Panel = ({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) => (
  <Card borderRadius="24px" bg="white" border="1px solid #eadfd5" boxShadow="0 16px 34px rgba(53, 36, 28, 0.08)" overflow="hidden">
    <CardHeader pb={2}>
      <Flex justify="space-between" align="flex-start" gap={3}>
        <Box>
          <Heading size="sm" color="#241c18">{title}</Heading>
          <Text fontSize="sm" color="#7a7068" mt={1}>{subtitle}</Text>
        </Box>
      </Flex>
    </CardHeader>
    <Divider borderColor="#efe5dc" />
    <CardBody>{children}</CardBody>
  </Card>
);

const EmptyState = ({ text }: { text: string }) => (
  <Flex minH="180px" align="center" justify="center" borderRadius="18px" bg="#faf7f3" border="1px dashed #d9c9ba">
    <Text color="#8a817a" fontSize="sm">{text}</Text>
  </Flex>
);

export default DashboardObrasPublicas;
